import { useEffect, useState } from "react";
import { core } from "@servicetitan/hammer-token";

const breakpoints = {
  sm: parseInt(core.primitive?.BreakpointSm.value),
  md: parseInt(core.primitive?.BreakpointMd.value),
  lg: parseInt(core.primitive?.BreakpointLg.value),
  xl: parseInt(core.primitive?.BreakpointXl.value),
  xxl: parseInt(core.primitive?.BreakpointXxl.value),
};

const areBreakpointsDefined = Object.values(breakpoints).every(
  (breakpoint) => breakpoint,
);

/**
 * Return type for the useBreakpoint hook
 */
export type BreakpointReturnProps = {
  /**
   * The breakpoint name
   */
  name: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /**
   * Minimum width for this breakpoint
   */
  min: number | undefined;
  /**
   * Maximum width for this breakpoint
   */
  max: number | undefined;
  /**
   * Current device orientation
   */
  orientation: "portrait" | "landscape";
  /**
   * Current viewport width in CSS pixels (from window.innerWidth)
   */
  innerWidth: number;
  /**
   * Current viewport height in CSS pixels (from window.innerHeight)
   */
  innerHeight: number;
  /**
   * True for all breakpoints (mobile-first: xs and above)
   */
  xs: boolean;
  /**
   * True when viewport width >= 640px (mobile-first: sm and above)
   */
  sm: boolean;
  /**
   * True when viewport width >= 768px (mobile-first: md and above)
   */
  md: boolean;
  /**
   * True when viewport width >= 1024px (mobile-first: lg and above)
   */
  lg: boolean;
  /**
   * True when viewport width >= 1280px (mobile-first: xl and above)
   */
  xl: boolean;
  /**
   * True when viewport width >= 1536px (mobile-first: xxl and above)
   */
  xxl: boolean;
};

/**
 * Calculates mobile-first breakpoint boolean values based on the current breakpoint name.
 * Each property is true when the viewport is at or above that breakpoint.
 *
 * @param name - The current breakpoint name
 * @returns Object with boolean flags for each breakpoint
 */
function getBreakpointFlags(name: BreakpointReturnProps["name"]) {
  const breakpointOrder = ["xs", "sm", "md", "lg", "xl", "xxl"] as const;
  const currentIndex = breakpointOrder.indexOf(name);

  return {
    xs: currentIndex >= 0,
    sm: currentIndex >= 1,
    md: currentIndex >= 2,
    lg: currentIndex >= 3,
    xl: currentIndex >= 4,
    xxl: currentIndex >= 5,
  };
}

/**
 * Determines if the current viewport matches a specific breakpoint
 * @param name - The breakpoint name
 * @param min - Minimum width for this breakpoint
 * @param max - Maximum width for this breakpoint
 * @returns Breakpoint information if matched, undefined otherwise
 */
function breakpoint(
  name: BreakpointReturnProps["name"],
  min?: number,
  max?: number,
): BreakpointReturnProps | undefined {
  if (globalThis.innerWidth == null || globalThis.innerHeight == null) {
    return undefined;
  }

  const viewportWidth = globalThis.innerWidth;
  const viewportHeight = globalThis.innerHeight;

  if (
    (min == null || viewportWidth >= min) &&
    (max == null || viewportWidth < max)
  ) {
    return {
      name,
      min,
      max,
      orientation: viewportHeight > viewportWidth ? "portrait" : "landscape",
      innerWidth: viewportWidth,
      innerHeight: viewportHeight,
      ...getBreakpointFlags(name),
    } as const;
  }

  return undefined;
}

/**
 * Determines the current breakpoint based on viewport width and hammer-token breakpoint values.
 *
 * Features:
 * - Validates that all required hammer-token breakpoint values are available
 * - Uses cascade logic to determine the appropriate breakpoint
 * - Returns the first matching breakpoint in order of preference
 * - Handles edge cases where breakpoint values are missing
 * - Uses window.innerWidth/innerHeight for viewport measurements
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 *
 * @returns Current breakpoint information or undefined if breakpoint values are unavailable
 */
const onResize = () => {
  if (!areBreakpointsDefined) {
    return;
  }

  const { sm, md, lg, xl, xxl } = breakpoints;

  return (
    breakpoint("xs", undefined, sm) ??
    breakpoint("sm", sm, md) ??
    breakpoint("md", md, lg) ??
    breakpoint("lg", lg, xl) ??
    breakpoint("xl", xl, xxl) ??
    breakpoint("xxl", xxl, undefined)
  );
};

/**
 * Custom hook for detecting current breakpoint and viewport information.
 *
 * Features:
 * - Detects current breakpoint based on viewport width
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 * - Provides mobile-first boolean props (e.g., `lg` is true for lg, xl, and xxl)
 * - Provides device orientation information
 * - Returns current viewport dimensions
 * - Uses hammer-token breakpoint values for consistency
 * - Listens for window resize events
 * - Supports optional disable functionality
 * - Uses window.innerWidth/innerHeight for viewport measurements
 *
 * @example
 * ```tsx
 * const breakpoint = useBreakpoint();
 * // Mobile-first approach:
 * <div className={cn({ 'p-block-4': breakpoint?.lg })} />
 * ```
 *
 * @param props - Optional configuration object
 * @param props.disable - Whether to disable the hook
 * @returns Current breakpoint information or undefined if disabled
 */
export const useBreakpoint = (props?: { disable?: boolean }) => {
  const [size, setSize] = useState(onResize);

  useEffect(() => {
    if (!areBreakpointsDefined || props?.disable) return;

    const updateSize = () => {
      setSize(onResize());
    };

    globalThis.addEventListener("resize", updateSize);

    return () => {
      globalThis.removeEventListener("resize", updateSize);
    };
  }, [props?.disable]);

  if (props?.disable) return;

  return size;
};
